/* eslint-disable max-lines */
/* eslint-disable complexity */
'use client';

import {
	Button,
	Flex,
	For,
	GridItem,
	HStack,
	Stack,
} from '@chakra-ui/react';
import { SelectInput } from '@packages/chakra-v3';

import { ProductCard, ProductFilterDialog, ProductListFallBack } from '../../components';
import useSpecialPrice from '../../hooks/useSpecialPrice';
import { useLocale, useTranslations } from 'next-intl';
import { useQueryParams } from '@/modules/shared';
import { WebContractListProductsQueryParamsSchema } from '@packages/contracts';
import type { Locale } from '@/libs/next-intl';
import React, { useRef } from 'react';
import { CustomDialog, DefaultStyle, ResponsiveAutoGrid } from '@packages/wastic-shared';

const useSpecialPriceC = () => {
	const locale = useLocale() as Locale;
	const isThai = locale === 'th';

	const { queryParams, setQueryParams, resetQueryParams } = useQueryParams({
		schema:        WebContractListProductsQueryParamsSchema,
		defaultValues: { onlyDiscount: 'true' as never },
	});
	const handleOnReset = () => {
		resetQueryParams();
		window.location.href = `${window.location.pathname}?onlyDiscount=true`;
	};

	const {
		isLoading, productList, filters, handleNavigateToProductDetail,
	} = useSpecialPrice(queryParams);

	return {
		isLoading,
		filters,
		queryParams,
		setQueryParams,
		handleOnReset,
		pagination: productList?.meta.pagination,
		products:   productList?.products
			? productList?.products.map(product => ({
				id:         product.id,
				name:       product.name,
				slug:       product.slug,
				coverImage: product.coverImage,
				price:      {
					amount:     isThai ? product.price : product.priceUsd,
					discounted: isThai ? product.discount?.discountPrice : product.discount?.discountPriceUsd,
					currency:   isThai ? 'THB' : 'USD',
				} as const,
				collection: {
					name: product.collectionName,
					slug: product.collectionSlug,
				},
			}))
			: [],
		handleNavigateToProductDetail,
	};
};

const SpecialPriceContainer = () => {
	const t = useTranslations('SpecialPricePage');
	const contentRef = useRef<HTMLDivElement>(null);

	const {
		isLoading, queryParams, filters, products, setQueryParams, handleOnReset, handleNavigateToProductDetail,
	} = useSpecialPriceC();

	const Fallback = <ProductListFallBack count={6} isLoading={isLoading} message={t('noItem')}  />;

	return (
		<Stack gap={DefaultStyle.MEDIUM_GAP}>
			{filters && (
				<Flex alignItems={'center'} justifyContent={'end'}>
					<HStack alignItems={'center'} gap={DefaultStyle.SMALL_GAP} hideFrom={'xl'}>
						<ProductFilterDialog ref={contentRef}>
							<CustomDialog.Body>
								<Stack gap={DefaultStyle.TIGHT_GRID_GAP} py={DefaultStyle.SMALL_GAP}>
									<SelectInput
										ref={contentRef}
										clearable
										collection={filters.frameShapeListCollection}
										defaultValue={queryParams.frameShape ? [ queryParams.frameShape ] : undefined}
										flex={1}
										label={'Frame shape'}
										minW={'180px'}
										size={'sm'}
										onValueChange={({ value }) => setQueryParams('frameShape', value[0])}
									/>
									<SelectInput
										ref={contentRef}
										clearable
										collection={filters.frameColorListCollection}
										defaultValue={queryParams.frameColor ? [ queryParams.frameColor ] : undefined}
										flex={1}
										label={'Frame color'}
										minW={'180px'}
										size={'sm'}
										onValueChange={({ value }) => setQueryParams('frameColor', value[0])}
									/>
									<SelectInput
										ref={contentRef}
										clearable
										collection={filters.lensColorListCollection}
										defaultValue={queryParams.lensColor ? [ queryParams.lensColor ] : undefined}
										flex={1}
										label={'Lens color'}
										minW={'180px'}
										size={'sm'}
										onValueChange={({ value }) => setQueryParams('lensColor', value[0])}
									/>
									<SelectInput
										ref={contentRef}
										clearable
										collection={filters.polarizedListCollection}
										defaultValue={queryParams.isPolarized !== undefined
											? [ String(queryParams.isPolarized) ]
											: undefined}
										flex={1}
										label={'Polarized'}
										minW={'180px'}
										size={'sm'}
										onValueChange={({ value }) => setQueryParams('isPolarized', value[0] as never)}
									/>
								</Stack>
							</CustomDialog.Body>
						</ProductFilterDialog>
						<Button
							minW={'100px'}
							size={'sm'}
							variant={'outline'}
							onClick={handleOnReset}
						>
							Reset
						</Button>
					</HStack>
					<Flex alignItems={'end'} gap={DefaultStyle.TIGHT_GRID_GAP} hideBelow={'xl'}>
						<SelectInput
							clearable
							collection={filters.frameShapeListCollection}
							defaultValue={queryParams.frameShape ? [ queryParams.frameShape ] : undefined}
							flex={1}
							label={'Frame shape'}
							minW={'180px'}
							size={'sm'}
							onValueChange={({ value }) => setQueryParams('frameShape', value[0])}
						/>
						<SelectInput
							clearable
							collection={filters.frameColorListCollection}
							defaultValue={queryParams.frameColor ? [ queryParams.frameColor ] : undefined}
							flex={1}
							label={'Frame color'}
							minW={'180px'}
							size={'sm'}
							onValueChange={({ value }) => setQueryParams('frameColor', value[0])}
						/>
						<SelectInput
							clearable
							collection={filters.lensColorListCollection}
							defaultValue={queryParams.lensColor ? [ queryParams.lensColor ] : undefined}
							flex={1}
							label={'Lens Color'}
							minW={'180px'}
							size={'sm'}
							onValueChange={({ value }) => setQueryParams('lensColor', value[0])}
						/>
						<SelectInput
							clearable
							collection={filters.polarizedListCollection}
							defaultValue={queryParams.isPolarized !== undefined ? [ String(queryParams.isPolarized) ] : undefined}
							flex={1}
							label={'Polarized'}
							minW={'180px'}
							size={'sm'}
							onValueChange={({ value }) => setQueryParams('isPolarized', value[0] as never)}
						/>
						<Button
							minW={'100px'}
							size={'sm'}
							variant={'outline'}
							onClick={handleOnReset}
						>Reset
						</Button>
					</Flex>
				</Flex>
			)}
			<ResponsiveAutoGrid gap={DefaultStyle.GRID_GAP}>
				<For each={products} fallback={Fallback}>
					{product => (
						<GridItem key={`feature-product-${product.id}`}>
							<ProductCard
								product={product}
								onClickProductCard={() => handleNavigateToProductDetail(product.collection.slug, product.slug)}
							/>
						</GridItem>
					)}
				</For>
			</ResponsiveAutoGrid>
		</Stack>
	);
};

export default SpecialPriceContainer;